import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { pushNotificationsAPI } from '../services/api';
import './Users.css';

const PushNotifications = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [dataJson, setDataJson] = useState('');
  const [sending, setSending] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadRecipients = useCallback(async () => {
    setLoadingRecipients(true);
    try {
      const res = await pushNotificationsAPI.recipients({
        search: debouncedSearch || undefined,
      });
      setRecipients(res.data?.data ?? []);
    } catch (error) {
      toast.error(
        'Could not load recipients: ' +
          (error.response?.data?.message || error.message),
      );
      setRecipients([]);
    } finally {
      setLoadingRecipients(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const allVisibleSelected = useMemo(() => {
    if (recipients.length === 0) return false;
    return recipients.every((u) => selectedIds.has(u.id));
  }, [recipients, selectedIds]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        recipients.forEach((u) => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        recipients.forEach((u) => next.add(u.id));
        return next;
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required.');
      return;
    }
    if (!sendToAll && selectedIds.size === 0) {
      toast.error('Select one or more users, or choose “send to all”.');
      return;
    }

    let data = undefined;
    if (dataJson.trim()) {
      try {
        data = JSON.parse(dataJson);
        if (typeof data !== 'object' || data === null || Array.isArray(data)) {
          throw new Error('Data must be a JSON object.');
        }
      } catch (err) {
        toast.error('Invalid JSON data: ' + (err.message || String(err)));
        return;
      }
    }

    setSending(true);
    try {
      const res = await pushNotificationsAPI.send({
        title: title.trim(),
        body: body.trim(),
        send_to_all: sendToAll,
        user_ids: sendToAll ? [] : Array.from(selectedIds),
        ...(data !== undefined ? { data } : {}),
      });
      const errs = res.data?.errors;
      const skipped = res.data?.users_without_token;
      if (errs && errs.length > 0) {
        toast.warning(
          `Expo reported issues (${res.data?.tickets ?? 0} ok tickets). Check console for details.`,
        );
        console.warn('Expo push errors:', errs);
      } else {
        toast.success(
          `Sent — devices: ${res.data?.recipient_devices ?? 0}, Expo tickets: ${res.data?.tickets ?? 0}`,
        );
      }
      if (skipped > 0) {
        toast.info(`${skipped} selected user(s) have no push token and were skipped.`);
      }
    } catch (error) {
      toast.error(
        'Send failed: ' +
          (error.response?.data?.message ||
            (error.response?.data?.errors &&
              JSON.stringify(error.response.data.errors)) ||
            error.message),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="users-container">
      <div className="table-container" style={{ padding: 20, maxWidth: 920 }}>
        <h3 style={{ marginBottom: 8 }}>Push notifications</h3>
        <p style={{ color: '#6b5c63', marginBottom: 20, fontSize: 14 }}>
          Sends via Expo Push Service. Only users who opened the app on a device and
          granted notifications appear below (token stored in the database).
        </p>

        <form onSubmit={submit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
              maxLength={255}
              required
            />
          </div>
          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification body"
              rows={4}
              maxLength={4000}
              required
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={sendToAll}
                onChange={(e) => {
                  setSendToAll(e.target.checked);
                  if (e.target.checked) setSelectedIds(new Set());
                }}
              />{' '}
              Send to <strong>all</strong> users with a registered push token
            </label>
          </div>

          {!sendToAll && (
            <>
              <div className="form-group">
                <label>Search recipients</label>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Email or name…"
                />
              </div>
              <p style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
                {selectedIds.size} selected · up to 500 matches shown
              </p>
              <div
                style={{
                  border: '1px solid #ebe6ea',
                  borderRadius: 8,
                  maxHeight: 360,
                  overflow: 'auto',
                  marginBottom: 16,
                  background: '#fff',
                }}
              >
                {loadingRecipients ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>
                ) : recipients.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    No users with a push token match this search.
                  </div>
                ) : (
                  <table style={{ margin: 0, width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 44 }}>
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleSelectAllVisible}
                            aria-label="Select all visible"
                          />
                        </th>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(u.id)}
                              onChange={() => toggleOne(u.id)}
                            />
                          </td>
                          <td>{u.email}</td>
                          <td>
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td>{u.user_type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label>Optional data (JSON object)</label>
            <textarea
              value={dataJson}
              onChange={(e) => setDataJson(e.target.value)}
              placeholder='e.g. {"screen":"orders"}'
              rows={3}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? 'Sending…' : 'Send notification'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PushNotifications;
